/** @jsxImportSource @emotion/react */
import { updatePassword } from 'api-client/accounts';
import { LoginInfoMessageEnum } from 'bc-model';
import { ODSAlert, ODSButton, ODSGroup, ODSInput, ODSLoginPortal } from 'bc-ods';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { includeBasePath } from 'utils/url-utils';
import yup from 'utils/yup';
import PasswordRequirements from './PasswordRequirements';

const messages = defineMessages({
  button: {
    id: 'Expired.button',
    defaultMessage: 'Update password',
  },
  signInWithAnotherAccount: {
    id: 'Expired.signInWithAnotherAccount',
    defaultMessage: 'Back to login',
  },
  passwordLabel: {
    id: 'Expired.passwordLabel',
    defaultMessage: 'Current password',
  },
  passwordFeedback: {
    id: 'Expired.passwordFeedback',
    defaultMessage: `{error, select,
      min {Must be at least 15 characters in length}
      lowercase {Must include at least 1 lowercase letter}
      uppercase {Must include at least 1 uppercase letter}
      digit {Must include at least 1 digit}
      symbol {Must include at least 1 symbol}
      match {Both passwords must match}
      previous {Cannot match your current password}
      other {Required}}`,
  },
  updateFeedback: {
    id: 'Expired.updateFeedback',
    defaultMessage:
      '{feedback, select, ' +
      '  invalid {Invalid password provided.}' +
      '  used {Password can not match your 5 latest previously used passwords}' +
      '  insufficient_change {New password must be significantly different from your current password}' +
      '  other {An error occurred. Please review the form and try again.}' +
      '}',
  },
  unknownError: {
    id: 'Expired.unknownError',
    defaultMessage: 'An unknown error occurred while processing your request. Please try again.',
  },
  matchedOldPassword: {
    id: 'Expired.matchedOldPassword',
    defaultMessage: 'New password can not match previous password.',
  },
});

function Expired(): ReactElement {
  const intl = useIntl();
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [showFormError, setShowError] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    password: yup.string().required('required'),
    newPassword: yup
      .string()
      .required('required')
      .min(15, 'min')
      .minLowercase(1, 'lowercase')
      .minUppercase(1, 'uppercase')
      .minNumbers(1, 'digit')
      .minSymbols(1, 'symbol')
      .notOneOf([yup.ref('password'), ''], 'previous'),
    confirmNewPassword: yup
      .string()
      .oneOf([yup.ref('newPassword'), ''], 'match')
      .required(),
  });

  const handleSubmit = async ({ password, newPassword }, { setSubmitting }) => {
    try {
      if (password === newPassword) {
        setFormError(intl.formatMessage(messages.matchedOldPassword));
        setShowError(true);
        setSubmitting(false);
        return;
      }

      await updatePassword(password, newPassword);

      // We use window.location.href instead of a <Redirect> as it triggers a full reload
      // Saving us from having to refetch the user now that they've been updated
      window.location.href = includeBasePath('/');
    } catch (error: any) {
      setFormError(
        intl.formatMessage(messages.updateFeedback, { feedback: error.response.data.error }) ??
          intl.formatMessage(messages.unknownError)
      );
      setShowError(true);
      setSubmitting(false);
    }
  };

  return (
    <ODSLoginPortal>
      <Formik
        initialValues={{ password: '', newPassword: '', confirmNewPassword: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, touched, errors }) => (
          <Form>
            {showFormError && (
              <ODSAlert color="danger" className="my-3">
                {formError}
              </ODSAlert>
            )}
            <ODSGroup stacked spacing="lg">
              <ValidatedFormikField
                as={ODSInput}
                name="password"
                id="password"
                type="password"
                autoFocus
                data-testid="password"
                autoComplete="current-password"
                label={<FormattedMessage {...messages.passwordLabel} />}
                error={
                  touched.password && (
                    <FormattedMessage
                      {...messages.passwordFeedback}
                      values={{ error: errors.password }}
                    />
                  )
                }
              />
              <PasswordRequirements
                passwordName="newPassword"
                confirmPasswordName="confirmNewPassword"
                reset
              />
              <ODSGroup fullWidth>
                <ODSButton cta fullWidth disabled={isSubmitting} type="submit" data-testid="submit">
                  <FormattedMessage {...messages.button} />
                </ODSButton>
              </ODSGroup>
              <ODSButton
                link={includeBasePath(
                  `/api/auth/logout?reason=${LoginInfoMessageEnum.CANCELED_PW_RESET}`
                )}
                quiet
                color="primary"
                className="w-100 text-decoration-underline"
                data-testid="back-to-login"
              >
                <FormattedMessage {...messages.signInWithAnotherAccount} />
              </ODSButton>
            </ODSGroup>
          </Form>
        )}
      </Formik>
    </ODSLoginPortal>
  );
}

export default Expired;
